import { Check, Loader2 } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  enhancedApi as externalTaskApi,
  type ExternalTaskListItemSchema,
} from '@/services/api/generated/externalTaskService';
import { formatTaskDate } from '@/utils/taskDateUtils';

interface ExternalTaskLinkingInterfaceProps {
  matterId: string;
  linkedExternalTaskIds: string[];
  onLink: (externalTaskIds: string[]) => Promise<void>;
  isLinking: boolean;
}

export const ExternalTaskLinkingInterface: React.FC<
  ExternalTaskLinkingInterfaceProps
> = ({ matterId, linkedExternalTaskIds, onLink, isLinking }) => {
  const [selectedTaskIds, setSelectedTaskIds] = React.useState<Set<string>>(
    new Set(),
  );

  // Fetch external tasks for this matter
  const { data: externalTasksData, isLoading } =
    externalTaskApi.useExternalTaskListQuery({
      matter: [matterId],
      limit: 100,
    });

  // Filter out already linked tasks
  const availableTasks = React.useMemo(() => {
    const tasks = externalTasksData?.results || [];
    const linkedSet = new Set(linkedExternalTaskIds);
    return tasks.filter((task) => !linkedSet.has(task.id));
  }, [externalTasksData?.results, linkedExternalTaskIds]);

  const handleToggleTask = (taskId: string) => {
    setSelectedTaskIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const handleLinkSelected = async () => {
    if (selectedTaskIds.size === 0) return;
    await onLink(Array.from(selectedTaskIds));
    setSelectedTaskIds(new Set());
  };

  const handleToggleAll = () => {
    if (selectedTaskIds.size === availableTasks.length) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(availableTasks.map((t) => t.id)));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const hasAvailableTasks = availableTasks.length > 0;
  const allSelected =
    hasAvailableTasks && selectedTaskIds.size === availableTasks.length;

  return (
    <div className="space-y-3">
      {/* Action buttons */}
      {selectedTaskIds.size > 0 && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-900">
            {selectedTaskIds.size} task{selectedTaskIds.size !== 1 ? 's' : ''}{' '}
            selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedTaskIds(new Set())}
              disabled={isLinking}
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleLinkSelected}
              disabled={isLinking}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLinking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Linking...
                </>
              ) : (
                `Link ${selectedTaskIds.size} task${selectedTaskIds.size !== 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Select all header */}
      {hasAvailableTasks && (
        <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-t-lg border border-slate-200 border-b-0">
          <button
            type="button"
            onClick={handleToggleAll}
            className={`flex-shrink-0 h-4 w-4 rounded border-2 flex items-center justify-center transition-colors ${
              allSelected
                ? 'bg-blue-600 border-blue-600'
                : 'border-slate-300 hover:border-slate-400'
            }`}
          >
            {allSelected && <Check className="h-3 w-3 text-white" />}
          </button>
          <span className="text-sm font-medium text-slate-700">
            {allSelected ? 'Deselect all' : 'Select all'} (
            {availableTasks.length} available)
          </span>
        </div>
      )}

      {/* Task list */}
      <div
        className={`border border-slate-200 ${hasAvailableTasks ? 'rounded-b-lg border-t-0' : 'rounded-lg'} max-h-[400px] overflow-y-auto`}
      >
        {!hasAvailableTasks && (
          <div className="p-8 text-center text-sm text-slate-500">
            No available external tasks to link
          </div>
        )}

        {availableTasks.map((task: ExternalTaskListItemSchema) => {
          const isSelected = selectedTaskIds.has(task.id);

          return (
            <button
              key={task.id}
              type="button"
              onClick={() => handleToggleTask(task.id)}
              className={`w-full px-3 py-2.5 flex items-start gap-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-b-0 ${
                isSelected ? 'bg-blue-50' : ''
              }`}
            >
              <div
                className={`flex-shrink-0 h-4 w-4 rounded border-2 flex items-center justify-center transition-colors mt-0.5 ${
                  isSelected
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                {isSelected && <Check className="h-3 w-3 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {task.name}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                    {task.status}
                  </span>
                  <span className="text-xs text-slate-500">{task.source}</span>
                  {task.due_date && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-xs text-slate-500">
                        Due {formatTaskDate(task.due_date, 'MMM d')}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
